import React from "react";
import Joi from "joi-browser";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { Tabs, Tab } from "react-bootstrap";
import {
  getAlias,
  addAlias,
  updateAlias,
} from "../../../common/services/products/aliasService";
import { getProducts } from "../../../common/services/products/productService";
import { getOrganizations } from "../../../common/services/auth/organizationService";
import Form from "../../../common/components/form/form";
import Loading from "../../../common/components/loading/loading";
import AliasHistory from "./aliasHistory";
import { withTranslation } from "react-i18next";

class Alias extends Form {
  state = {
    data: {
      aliasId: "",
      name: "",
      productId: "",
      organizationId: "",
      active: "",
      eventId: "",
    },
    createdDate: "",
    updatedDate: "",
    products: [],
    organizations: [],
    errors: {},
    isLoading: true,
    isNew: true,
  };

  schema = {
    aliasId: Joi.string(),
    name: Joi.string().required().label(this.props.t("AliasName")),
    productId: Joi.string().required().label(this.props.t("Product")),
    organizationId: Joi.array()
      .items(Joi.object())
      .min(1)
      .max(1)
      .required()
      .label(this.props.t("Organization")),
    active: Joi.boolean().label(this.props.t("Active")),
    eventId: Joi.string(),
  };

  async componentDidMount() {
    const { aliasId, isNew } = this.props;
    let alias = {};

    if (!isNew) {
      alias = await getAlias(aliasId);
      alias.eventId = uuid();
    } //New User Add
    else {
      alias = {
        aliasId: aliasId,
        name: "",
        productId: "",
        organizationId: "",
        active: true,
        eventId: uuid(),
      };
    }

    // Resolve the existing organization only after the typeahead options are available.
    const [allProducts, allOrganizations] = await Promise.all([
      getProducts(),
      getOrganizations(),
    ]);
    const products = allProducts.filter(
      (x) => x.active || x.productId === alias.productId
    );
    const organizations = allOrganizations.filter(
      (x) => x.active || x.organizationId === alias.organizationId
    );

    if (!isNew) {
      alias.organizationId = organizations.find(
        (x) => x.organizationId === alias.organizationId
      );
    }

    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    this.setState({
      data: this.mapToViewModel(alias),
      isLoading: false,
      isNew,
      products,
      organizations,
      createdDate: new Date(alias.createdDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      updatedDate: new Date(alias.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
    });
  }

  mapToViewModel(alias) {
    return {
      aliasId: alias.aliasId,
      name: alias.name,
      productId: alias.productId,
      organizationId: [alias.organizationId],
      active: alias.active,
      eventId: alias.eventId,
    };
  }

  doSubmit = async () => {
    const { t, onRename } = this.props;
    const { isNew } = this.state;

    try {
      //Reset the event ID
      const data = { ...this.state.data };
      data.eventId = uuid();
      this.setState({ data: data });

      //Just updating data after the setState is causing an asynch colision, this avoids that.
      var submitData = { ...data };
      submitData.organizationId = data.organizationId[0].organizationId; //force back to a single object from array before passing to API.

      let response = {};

      if (!isNew) {
        response = await updateAlias(submitData);
        toast.success(t("AliasUpdated"));
      } else {
        response = await addAlias(submitData);
        this.setState({ isNew: false });
        onRename(this.state.data.aliasId, this.state.data.name);
        toast.success(t("AliasAdded"));
      }

      //Call back to the select list to allow it to update.
      this.props.submitCallbackFunction(response);
    } catch (ex) {
      console.log(t("ErrorSaving"), ex);

      if (ex.response && ex.response.status === 400) {
        var errorList = ex.response.data.errors;
        if (errorList) {
          var errorKeys = Object.keys(ex.response.data.errors);
          errorKeys.forEach(function (key) {
            errorList[key].map((error) => {
              toast.error(t("ErrorSaving") + " " + key + ": " + error);
              return null;
            });
          });
        } else {
          toast.error(t("ErrorSaving") + " " + ex.response.data);
        }
        const errors = { ...this.state.errors };
        errors.Name = ex.response.data;
        toast.error(ex.response.data.errors.title);
        this.setState({ errors });
      }
    }
  };

  render() {
    const { isLoading, isNew, createdDate, updatedDate } = this.state;

    if (isLoading) return <Loading />;

    const { t, aliasId, allowEdit } = this.props;

    return (
      <div className="container-fluid">
        <form className="form-inline" onSubmit={this.handleSubmit}>
          <fieldset disabled={allowEdit ? "" : "disabled"}>
            <div>
              {this.renderSwitch("active", t("Active"))}
              {this.renderInput("name", t("AliasName"))}
              {this.renderSelect(
                "productId",
                t("Product"),
                this.state.products,
                "productId"
              )}
              {this.renderTypeahead(
                "organizationId",
                t("Organization"),
                this.state.organizations,
                false, //not disabled
                "organizationId"
              )}
              {allowEdit && this.renderButton(t("Save"))}
              <div className="form-footnote">
                {createdDate !== "Invalid Date" && (
                  <div>
                    {t("Created")} {createdDate}
                  </div>
                )}
                {updatedDate !== "Invalid Date" && (
                  <div>
                    {t("Updated")} {updatedDate}
                  </div>
                )}
              </div>
            </div>
          </fieldset>
        </form>
        {!isNew && (
          <div>
            <Tabs
              className="nav-dark nav-tabs-sm"
              defaultActiveKey={1}
              id="useraccount-tabs"
              mountOnEnter={true}
              unmountOnExit={true}
            >
              <Tab eventKey={1} title={t("History")}>
                <AliasHistory aliasId={aliasId} />
              </Tab>
            </Tabs>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation(["products"])(Alias);
